import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthContext } from './useAuthContext';

const useSignupPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const { user } = useAuthContext();
  const [exitIntentShown, setExitIntentShown] = useState(false);
  const hasScrolled = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const timeoutsRef = useRef([]);

  // Check if popup should be shown based on various conditions
  const shouldShowPopup = useCallback(() => {
    // Don't show if user is already signed in
    if (user) return false;

    if (localStorage.getItem('signup_popup_completed') === 'true') return false;

    const signupStarted = localStorage.getItem('signup_popup_started');
    if (signupStarted) {
      const timeSinceStarted = Date.now() - parseInt(signupStarted);
      if (timeSinceStarted < 2 * 60 * 60 * 1000) { // 2 hours
        return false;
      }
    }

    // Don't show if user has dismissed popup in last 24 hours
    const dismissedTime = localStorage.getItem('signup_popup_dismissed');
    if (dismissedTime) {
      const timeSinceDismissed = Date.now() - parseInt(dismissedTime);
      if (timeSinceDismissed < 24 * 60 * 60 * 1000) { // 24 hours
        return false;
      }
    }

    // Don't show if user has closed popup in last 24 hours
    const closedTime = localStorage.getItem('signup_popup_closed');
    if (closedTime) {
      const timeSinceClosed = Date.now() - parseInt(closedTime);
      if (timeSinceClosed < 24 * 60 * 60 * 1000) { // 24 hours
        return false;
      }
    }

    return true;
  }, [user]);

  // Show popup if conditions are met
  const showPopupIfEligible = useCallback(() => {
    if (shouldShowPopup()) {
      setShowPopup(true);
    }
  }, [shouldShowPopup]);

  // Force show popup (for external triggers like calculator)
  const forceShowPopup = useCallback(() => {
    setShowPopup(true);
  }, []);

  // Handle scroll event
  const handleScroll = useCallback(() => {
    if (!hasScrolled.current) {
      hasScrolled.current = true;
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Show popup after 10 seconds of scroll
      scrollTimeoutRef.current = setTimeout(() => {
        showPopupIfEligible();
      }, 10000); // 10 seconds
    }
  }, [showPopupIfEligible]);

  // Handle exit intent (desktop only)
  const handleExitIntent = useCallback((e) => {
    // Independent exit-intent: always show (once), unless logged in
    if (window.innerWidth <= 768) return;
    if (user) return;
    if (exitIntentShown) return;
    if (e.clientY <= 0) {
      setExitIntentShown(true);
      setShowPopup(true);
    }
  }, [exitIntentShown, user]);

  // Close popup
  const closePopup = () => {
    setShowPopup(false);
  };

  // Handle signup success
  const handleSignupSuccess = () => {
    localStorage.setItem('signup_popup_completed', 'true');
    localStorage.setItem('signed_in', 'true');
    setShowPopup(false);
  };

  // Handle signup start
  const handleSignupStart = () => {
    localStorage.setItem('signup_popup_started', Date.now().toString());
  };

  // Setup time-based triggers (2 minutes and 10 minutes)
  useEffect(() => {
    if (user) return; // Don't set up triggers for signed-in users

    // 2 minutes trigger
    const twoMinTimeout = setTimeout(() => {
      showPopupIfEligible();
    }, 2 * 60 * 1000); // 2 minutes

    // 10 minutes trigger
    const tenMinTimeout = setTimeout(() => {
      showPopupIfEligible();
    }, 10 * 60 * 1000); // 10 minutes

    timeoutsRef.current = [twoMinTimeout, tenMinTimeout];

    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    };
  }, [user, showPopupIfEligible]);

  // Setup scroll listener
  useEffect(() => {
    if (user) return; // Don't set up triggers for signed-in users

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [user, handleScroll]);

  // Setup exit intent listener
  useEffect(() => {
    if (user) return; // Don't set up triggers for signed-in users

    document.addEventListener('mouseleave', handleExitIntent);

    return () => {
      document.removeEventListener('mouseleave', handleExitIntent);
    };
  }, [user, handleExitIntent]);

  // Reset when user signs in
  useEffect(() => {
    if (user) {
      setShowPopup(false);
      hasScrolled.current = false;
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    }
  }, [user]);

  return {
    showPopup,
    closePopup,
    handleSignupSuccess,
    handleSignupStart,
    forceShowPopup,
  };
};

export default useSignupPopup;
